import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { ALBUM_KIND, buildAlbumTags, generateAlbumSlug } from '@/lib/album-utils';

interface CreateAlbumOptions {
  title: string;
  description?: string;
  image?: string;
  photoIds: string[];
  themeCoordinate?: string;
}

interface UpdateAlbumOptions {
  dTag: string;
  title: string;
  description?: string;
  image?: string;
  photoIds: string[];
  themeCoordinate?: string;
}

/**
 * Provides actions for creating, updating, and deleting albums.
 */
export function useAlbumActions() {
  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const queryClient = useQueryClient();

  const createAlbum = useCallback(async (opts: CreateAlbumOptions) => {
    if (!user) throw new Error('Must be logged in to create an album');

    const dTag = generateAlbumSlug(opts.title);
    const tags = buildAlbumTags({
      dTag,
      title: opts.title,
      description: opts.description,
      image: opts.image,
      photoIds: opts.photoIds,
      themeCoordinate: opts.themeCoordinate,
    });

    await publishEvent({
      kind: ALBUM_KIND,
      content: '',
      tags,
    });

    // Invalidate album queries
    queryClient.invalidateQueries({ queryKey: ['albums'] });

    return dTag;
  }, [user, publishEvent, queryClient]);

  const updateAlbum = useCallback(async (opts: UpdateAlbumOptions) => {
    if (!user) throw new Error('Must be logged in to update an album');

    const tags = buildAlbumTags({
      dTag: opts.dTag,
      title: opts.title,
      description: opts.description,
      image: opts.image,
      photoIds: opts.photoIds,
      themeCoordinate: opts.themeCoordinate,
    });

    await publishEvent({
      kind: ALBUM_KIND,
      content: '',
      tags,
    });

    queryClient.invalidateQueries({ queryKey: ['albums'] });
  }, [user, publishEvent, queryClient]);

  const deleteAlbum = useCallback(async (dTag: string) => {
    if (!user) throw new Error('Must be logged in to delete an album');

    const aCoordinate = `${ALBUM_KIND}:${user.pubkey}:${dTag}`;

    await publishEvent({
      kind: 5,
      content: '',
      tags: [
        ['a', aCoordinate],
        ['k', String(ALBUM_KIND)],
      ],
    });

    queryClient.invalidateQueries({ queryKey: ['albums'] });
  }, [user, publishEvent, queryClient]);

  return { createAlbum, updateAlbum, deleteAlbum };
}
